// services/usdaService.js
import { supabase } from '../lib/supabase';

class USDAService {
  constructor() {
    this.isDebugMode = process.env.NODE_ENV === 'development';
    
    // Determine base URL
    const isLocalhost = window.location.hostname === 'localhost';
    const apiPath = '/api/foods';
    
    // For localhost, use the full URL with port
    this.baseUrl = isLocalhost ? `http://localhost:3001${apiPath}` : apiPath;
    
    if (this.isDebugMode) {
      console.log('USDA Service initialized:', {
        environment: process.env.NODE_ENV,
        baseUrl: this.baseUrl,
        fullUrl: isLocalhost ? this.baseUrl : window.location.origin + this.baseUrl
      });
    }
  }

  /**
   * Primary search method
   */
  async searchFoods(query, options = {}) {
    const {
      pageSize = 20,
      page = 0,
      lastId = null
    } = options;

    if (this.isDebugMode) {
      console.log('Starting food search:', { query, pageSize, page, lastId });
    }

    try {
      // 1) Search local database
      let localQuery = supabase
        .from('usda_foods')
        .select(`
          id,
          fdc_id,
          data_type,
          description,
          scientific_name,
          brand_owner,
          ingredients,
          serving_size,
          serving_unit,
          usda_food_nutrients (
            nutrient_id,
            nutrient_name,
            nutrient_number,
            unit_name,
            value
          )
        `)
        .ilike('description', `%${query}%`)
        .in('data_type', ['Foundation', 'Survey (FNDDS)']);

      if (lastId) {
        localQuery = localQuery.gt('id', lastId);
      }

      const { data: localFoods, error: localError } = await localQuery
        .order('id', { ascending: true })
        .limit(pageSize);

      if (localError) throw localError;

      // 2) Map local foods
      const mappedLocalFoods = (localFoods || []).map((food) => ({
        fdcId: parseInt(food.fdc_id),
        dataType: food.data_type,
        description: food.description,
        scientificName: food.scientific_name,
        brandOwner: food.brand_owner,
        ingredients: food.ingredients,
        servingSize: food.serving_size,
        servingUnit: food.serving_unit,
        source: 'local',
        id: food.id,
        calories: this.getNutrientValue(food.usda_food_nutrients, [208, 957, 958]),
        protein_g: this.getNutrientValue(food.usda_food_nutrients, [203]),
        carbs_g: this.getNutrientValue(food.usda_food_nutrients, [205]),
        fat_g: this.getNutrientValue(food.usda_food_nutrients, [204]),
        foodNutrients: food.usda_food_nutrients || []
      }));

      // Return if we have enough local results
      if (mappedLocalFoods.length >= pageSize) {
        return {
          foods: mappedLocalFoods,
          pagination: {
            hasMore: true,
            nextPage: page + 1,
            lastId: mappedLocalFoods[mappedLocalFoods.length - 1]?.id
          }
        };
      }

      // 3) Try USDA API for remaining results
      try {
        const remainingSize = pageSize - mappedLocalFoods.length;
        const apiUrl = `${this.baseUrl}/search?query=${encodeURIComponent(query)}&pageSize=${remainingSize}&pageNumber=${page}`;
        
        if (this.isDebugMode) {
          console.log('Fetching from API:', apiUrl);
        }

        const response = await fetch(apiUrl, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }

        const apiData = await response.json();
        const brandedFoods = (apiData.foods || []).map(food => ({
          fdcId: food.fdcId,
          dataType: food.dataType,
          description: food.description,
          brandOwner: food.brandOwner || '',
          ingredients: food.ingredients || '',
          servingSize: food.servingSize || 100,
          servingUnit: food.servingSizeUnit || 'g',
          source: 'api',
          calories: this.getUSDAApiNutrientValue(food.foodNutrients, [208, 957, 958]),
          protein_g: this.getUSDAApiNutrientValue(food.foodNutrients, [203]),
          carbs_g: this.getUSDAApiNutrientValue(food.foodNutrients, [205]),
          fat_g: this.getUSDAApiNutrientValue(food.foodNutrients, [204]),
          foodNutrients: food.foodNutrients || []
        }));

        const combinedFoods = [...mappedLocalFoods, ...brandedFoods];
        
        if (this.isDebugMode) {
          console.log('Search completed:', {
            localResults: mappedLocalFoods.length,
            apiResults: brandedFoods.length,
            total: combinedFoods.length
          });
        }

        return {
          foods: combinedFoods,
          pagination: {
            hasMore: combinedFoods.length === pageSize,
            nextPage: page + 1,
            lastId: combinedFoods[combinedFoods.length - 1]?.id
          }
        };

      } catch (apiError) {
        console.error('API Error:', apiError);
        // Return local results if API fails
        return {
          foods: mappedLocalFoods,
          pagination: {
            hasMore: false,
            nextPage: page + 1,
            lastId: mappedLocalFoods[mappedLocalFoods.length - 1]?.id
          }
        };
      }
    } catch (error) {
      console.error('Search error:', error);
      throw error;
    }
  }

  /**
   * Helper methods for nutrient calculations
   */
  getNutrientValue(nutrients, targetNumbers) {
    for (const num of targetNumbers) {
      const match = (nutrients || []).find(n => String(n.nutrient_number) === String(num));
      if (match) {
        return parseFloat(match.value) || 0;
      }
    }
    return 0;
  }

  getUSDAApiNutrientValue(nutrients, targetNumbers) {
    for (const num of targetNumbers) {
      const match = (nutrients || []).find(n => String(n.nutrientNumber) === String(num));
      if (match) {
        return parseFloat(match.value) || 0;
      }
    }
    return 0;
  }

  async clearCache() {
    // Implement if needed
    return Promise.resolve();
  }
}

export const usdaService = new USDAService();